import { useState } from "react";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { deleteWithUndo } from "@/lib/undo";

const PRESET_COLORS = [
  "#EF4444","#F97316","#F59E0B","#EAB308","#84CC16","#22C55E","#10B981",
  "#14B8A6","#06B6D4","#0EA5E9","#3B82F6","#6366F1","#8B5CF6","#A855F7",
  "#D946EF","#EC4899","#F43F5E","#94A3B8",
];

export default function CategoriesPage() {
  const { categories, createCategory, updateCategory, deleteCategory, restoreCategory } = useData();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [kind, setKind] = useState("general");

  const openNew = () => { setEditing(null); setName(""); setColor("#3B82F6"); setKind("general"); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setColor(c.color); setKind(c.kind); setOpen(true); };

  const save = async () => {
    if (!name.trim()) return toast.error("Naam is vereist");
    if (editing) {
      await updateCategory(editing.id, { name, color, kind });
      toast.success("Categorie bijgewerkt");
    } else {
      await createCategory({ name, color, kind });
      toast.success("Categorie aangemaakt");
    }
    setOpen(false);
  };

  return (
    <div className="p-5 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Levensgebieden</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">Categorieën</h1>
          <div className="text-sm text-slate-500 mt-1">Kleur je hele leven.</div>
        </div>
        <Button data-testid="category-new" onClick={openNew} className="bg-blue-600 hover:bg-blue-500 gap-2">
          <Plus size={16} /> Nieuwe categorie
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {categories.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-800 bg-[#121C2F] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-3.5 h-3.5 rounded-full" style={{ background: c.color }} />
                <span className="text-slate-100 font-medium">{c.name}</span>
              </div>
              <div className="flex gap-1">
                <button data-testid={`category-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1 text-slate-500 hover:text-slate-200">
                  <PencilSimple size={14} />
                </button>
                <button data-testid={`category-delete-${c.id}`} onClick={() => deleteWithUndo({
                  label: `Categorie "${c.name}" verwijderd`,
                  deleteFn: async () => { await deleteCategory(c.id); },
                  restoreFn: async () => { await restoreCategory(c); },
                })} className="p-1 text-slate-500 hover:text-rose-400">
                  <Trash size={14} />
                </button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2 uppercase tracking-widest">{c.kind}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-50">{editing ? "Categorie bewerken" : "Nieuwe categorie"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Naam</Label>
              <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1.5 bg-slate-900 border-slate-800" placeholder="bijv. Biologie" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Soort</Label>
              <Input value={kind} onChange={(e) => setKind(e.target.value)}
                className="mt-1.5 bg-slate-900 border-slate-800" placeholder="school, werk, vak..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Kleur</Label>
              <div className="mt-2 grid grid-cols-9 gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    data-testid={`category-color-${c}`}
                    onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full ring-2 transition-all ${color === c ? "ring-white scale-110" : "ring-transparent hover:scale-105"}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/60">Annuleren</Button>
            <Button data-testid="category-save" onClick={save} className="bg-blue-600 hover:bg-blue-500">Opslaan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
